// utilities/ticketVariants.js
//
// PURE logic for workflow VARIANTS on the ticket side (SOUND-785, plan
// 2026-10-14 Phase 2): the create-modal picker, carrying intake answers across a
// variant switch, the variant shown on a ticket, and the list's variant filter.
// sound-app has no component harness (SOUND-679), so every decision a screen
// makes about variants lives here and is pinned by ticketVariants.test.js.
//
// The API contract (sound-api utilities/ticketTemplateVariants.mjs):
//   - every template read carries `variantKey` (legacy rows read "default"),
//     `variantLabel` (nullable on legacy rows) and `isDefault`;
//   - GET /ticket-templates/variants?requestType=X → live variants, default
//     first, plus `defaultTemplateId`;
//   - a ticket stores `templateId` (one VERSION) and `variantKey` (survives new
//     versions; null on tickets created before SOUND-785 and on Telegram ones).

export const DEFAULT_VARIANT_KEY = "default";

/** A template's variantKey, with a row that has none read as "default". */
export function variantKeyOf(template) {
  return template?.variantKey || DEFAULT_VARIANT_KEY;
}

function humanizeKey(key) {
  return String(key || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The name to show for a template's variant. Legacy rows have no label; the
 * request type's own label is what the API renders `{{variant}}` as for them,
 * so it is the honest fallback here too.
 */
export function variantDisplayLabel(template, requestTypeLabel) {
  if (template?.variantLabel) return template.variantLabel;
  if (variantKeyOf(template) === DEFAULT_VARIANT_KEY && requestTypeLabel) return requestTypeLabel;
  return humanizeKey(variantKeyOf(template)) || "Default";
}

// ── Create modal: the picker ────────────────────────────────────────────────

/**
 * Whether the create modal shows a variant picker.
 *
 * Only for an INTERNAL user creating a top-level ticket (sub-task mode has no
 * request type), and only when MORE THAN ONE variant is live — with one, there
 * is nothing to choose and the modal behaves exactly as before variants.
 * Clients never pick (CPO decision 4).
 */
export function shouldShowVariantPicker({ internal, isSubtask = false, variants = [] } = {}) {
  return !!internal && !isSubtask && Array.isArray(variants) && variants.length > 1;
}

/**
 * The variant preselected when the list arrives: the API's `defaultTemplateId`
 * when it is in the list, else a row flagged default, else the first row (the
 * API sorts default first). Null when nothing is live.
 */
export function initialVariantSelection(variants = [], defaultTemplateId = null) {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  if (defaultTemplateId && variants.some((v) => v.templateId === defaultTemplateId)) {
    return defaultTemplateId;
  }
  const flagged = variants.find((v) => v.isDefault === true);
  return (flagged || variants[0]).templateId || null;
}

/**
 * After a re-fetch, keep the user's pick if it is still live; otherwise fall
 * back to the default. Never silently keep an id the API no longer serves.
 */
export function reconcileVariantSelection(current, variants = [], defaultTemplateId = null) {
  if (current && variants.some((v) => v.templateId === current)) return current;
  return initialVariantSelection(variants, defaultTemplateId);
}

/** The selected template row, or null. */
export function selectedVariant(variants = [], templateId) {
  if (!templateId) return null;
  return (variants || []).find((v) => v.templateId === templateId) || null;
}

/**
 * The `templateId` to put on POST /tickets, or undefined to omit it.
 *
 * Sent ONLY when the picker was shown (internal, 2+ live variants) and the pick
 * is one of the served rows. Omitted otherwise, so the server runs the default —
 * which is how clients, sub-tasks and single-variant types have always behaved.
 * A client sending a templateId gets 403 VARIANT_SELECTION_INTERNAL_ONLY, so the
 * guard here is the first of two refusals, not the only one.
 */
export function templateIdForCreate({ internal, isSubtask = false, variants = [], selectedTemplateId } = {}) {
  if (!shouldShowVariantPicker({ internal, isSubtask, variants })) return undefined;
  return selectedVariant(variants, selectedTemplateId) ? selectedTemplateId : undefined;
}

/** "Existing Disputifier instance (default)" — the picker's option text. */
export function variantOptionLabel(template, requestTypeLabel) {
  const label = variantDisplayLabel(template, requestTypeLabel);
  return template?.isDefault ? `${label} (default)` : label;
}

// ── Create modal: carrying answers across a variant switch ──────────────────

const OPTION_TYPES = ["select", "select-other"];

function valueFitsField(field, value) {
  if (!OPTION_TYPES.includes(field.type)) return true;
  if (value === undefined || value === null || value === "") return true;
  return Array.isArray(field.options) && field.options.includes(value);
}

/**
 * Intake answers to keep when the user switches from one variant to another.
 *
 * THE RULE: an answer survives only when the NEW variant asks the SAME question
 * — a field with the same `key` AND the same `type`. For a dropdown the answer
 * must also still be one of the new field's options. A `select-other`'s free
 * text (stored under its `otherKey`) travels with it only when the new field
 * uses the same otherKey. A ref field's display label travels with its id.
 * Everything else is dropped, so a hidden, stale answer can never be submitted
 * under a field the user did not see.
 *
 * Same key with a different type is treated as a DIFFERENT question: "amount"
 * as text vs. number, or a boolean vs. a dropdown, cannot be assumed equal.
 *
 * @param {{fromFields?: Object[], toFields?: Object[], values?: Object, labels?: Object}} args
 * @returns {{values: Object, labels: Object, dropped: string[]}}
 */
export function carryOverIntake({ fromFields = [], toFields = [], values = {}, labels = {} } = {}) {
  const fromByKey = new Map((fromFields || []).filter((f) => f?.key).map((f) => [f.key, f]));
  const outValues = {};
  const outLabels = {};
  const kept = new Set();

  for (const to of toFields || []) {
    if (!to?.key) continue;
    const from = fromByKey.get(to.key);
    if (!from || from.type !== to.type) continue;
    if (!Object.prototype.hasOwnProperty.call(values, to.key)) continue;
    const v = values[to.key];
    if (!valueFitsField(to, v)) continue;
    outValues[to.key] = v;
    kept.add(to.key);
    if (Object.prototype.hasOwnProperty.call(labels, to.key)) outLabels[to.key] = labels[to.key];
    if (
      to.type === "select-other" &&
      to.otherKey &&
      to.otherKey === from.otherKey &&
      Object.prototype.hasOwnProperty.call(values, to.otherKey)
    ) {
      outValues[to.otherKey] = values[to.otherKey];
      kept.add(to.otherKey);
    }
  }

  const dropped = Object.keys(values || {}).filter((k) => !kept.has(k));
  return { values: outValues, labels: outLabels, dropped };
}

// ── Ticket display ──────────────────────────────────────────────────────────

/**
 * Distinct variants among templates (any state), keyed `requestType → variantKey`.
 * A variant's label comes from its LIVE row when it has one, else its most
 * recently updated row — the same rule the API uses (variantsOf).
 * @returns {Map<string, Map<string, {variantKey, variantLabel, active}>>}
 */
export function variantsByRequestType(templates = []) {
  const sorted = [...(templates || [])].sort((a, b) => {
    if ((a.active === true) !== (b.active === true)) return a.active ? -1 : 1;
    return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
  });
  const out = new Map();
  for (const t of sorted) {
    if (!t?.requestTypeKey) continue;
    if (!out.has(t.requestTypeKey)) out.set(t.requestTypeKey, new Map());
    const byKey = out.get(t.requestTypeKey);
    const k = variantKeyOf(t);
    if (!byKey.has(k)) byKey.set(k, { variantKey: k, variantLabel: t.variantLabel ?? null, active: t.active === true });
    else if (!byKey.get(k).variantLabel && t.variantLabel) byKey.get(k).variantLabel = t.variantLabel;
  }
  return out;
}

/**
 * The variant to show on a ticket, or null to show nothing.
 *
 * THE RULE: show the variant when it tells the reader something — the ticket's
 * request type has MORE THAN ONE variant (live or not; a retired variant still
 * explains an old ticket), OR the ticket's variant is not "default". A type
 * that has only ever had its one original workflow shows nothing, which is
 * every ticket in prod until someone authors a second variant.
 *
 * The ticket's variant is its `variantKey`; tickets created before SOUND-785
 * have none, so it is recovered from the template VERSION they ran
 * (`templateId`). A ticket with neither ran no workflow (Telegram intake, a
 * type with no template) and has no variant.
 *
 * @param {Object} ticket - { requestType, variantKey?, templateId? }
 * @param {Object[]} templates - templates (any state) — at least the ticket's type
 * @param {string} [requestTypeLabel]
 * @returns {{variantKey: string, label: string}|null}
 */
export function ticketVariantDisplay(ticket, templates = [], requestTypeLabel) {
  if (!ticket) return null;
  const ran = ticket.templateId ? (templates || []).find((t) => t.templateId === ticket.templateId) : null;
  const key = ticket.variantKey || (ran ? variantKeyOf(ran) : null);
  if (!key) return null;
  const variants = variantsByRequestType(templates).get(ticket.requestType) || new Map();
  if (!(variants.size > 1 || key !== DEFAULT_VARIANT_KEY)) return null;
  const known = variants.get(key);
  const label =
    known?.variantLabel ||
    ran?.variantLabel ||
    (key === DEFAULT_VARIANT_KEY && requestTypeLabel ? requestTypeLabel : humanizeKey(key));
  return { variantKey: key, label };
}

// ── Ticket list: the variant filter's options ───────────────────────────────

/**
 * Options for the tickets list's Variant filter: `[{ value, label }]`.
 *
 * The filter sends `variantKey`, which is unique only WITHIN a request type
 * ("default" exists under every type). So:
 *   - with exactly one request type filtered, list THAT type's variants by
 *     their own labels;
 *   - otherwise list every distinct key, naming the types that use it, so
 *     "default" reads as what it is rather than as one type's workflow.
 * Request types with a single, default-only variant contribute nothing in the
 * unfiltered view — picking "default" there would just mean "any ticket that
 * ran a workflow", which the Request Type filter already expresses better.
 *
 * @param {Object[]} templates
 * @param {{requestTypeFilter?: string, rtLabel?: (key: string) => string}} [opts]
 */
export function variantFilterOptions(templates = [], { requestTypeFilter = "", rtLabel = (k) => k } = {}) {
  const byType = variantsByRequestType(templates);
  const typeKeys = String(requestTypeFilter || "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);

  if (typeKeys.length === 1) {
    const variants = byType.get(typeKeys[0]);
    if (!variants) return [];
    return [...variants.values()]
      .map((v) => ({
        value: v.variantKey,
        label: v.variantLabel || (v.variantKey === DEFAULT_VARIANT_KEY ? rtLabel(typeKeys[0]) : humanizeKey(v.variantKey)),
      }))
      .sort((a, b) =>
        a.value === DEFAULT_VARIANT_KEY ? -1 : b.value === DEFAULT_VARIANT_KEY ? 1 : a.label.localeCompare(b.label)
      );
  }

  const byKey = new Map(); // variantKey → { labels:Set, types:Set }
  for (const [typeKey, variants] of byType) {
    if (variants.size < 2 && variants.has(DEFAULT_VARIANT_KEY)) continue;
    for (const v of variants.values()) {
      if (!byKey.has(v.variantKey)) byKey.set(v.variantKey, { labels: new Set(), types: new Set() });
      const e = byKey.get(v.variantKey);
      if (v.variantLabel) e.labels.add(v.variantLabel);
      e.types.add(typeKey);
    }
  }
  return [...byKey.entries()]
    .map(([key, e]) => {
      const types = [...e.types].map((k) => rtLabel(k)).sort();
      const base =
        key === DEFAULT_VARIANT_KEY
          ? "Default workflow"
          : e.labels.size === 1
          ? [...e.labels][0]
          : humanizeKey(key);
      return { value: key, label: `${base} — ${types.join(", ")}` };
    })
    .sort((a, b) =>
      a.value === DEFAULT_VARIANT_KEY ? -1 : b.value === DEFAULT_VARIANT_KEY ? 1 : a.label.localeCompare(b.label)
    );
}
